<template>
  <div class="step-question-template blood-pressure-v2-us" data-testid="blood-pressure-range-question">
    <div class="blood-pressure-v2-us__title">What was your last blood pressure reading?</div>

    <div class="blood-pressure-v2-us__description">
      To be able to evaluate you for treatment, we need to know a reading of your blood pressure taken in the last six
      months.
    </div>

    <div class="blood-pressure-v2-us__question-title">1. What is your systolic number? (the top number)</div>

    <ul class="blood-pressure-v2-us__options">
      <li v-for="(option, optionIndex) in question.options.systolic" :key="optionIndex">
        <label
          :for="'blood-pressure-v2-us__options-systolic-'.concat(optionIndex)"
          :class="{ selected: systolic === option.range }"
        >
          <input
            :id="'blood-pressure-v2-us__options-systolic-'.concat(optionIndex)"
            type="radio"
            v-on:click="selectSystolicRange(option.range)"
            :checked="systolic === option.range"
          />
          {{ option.label }}
          <span
            :class="'blood-pressure-v2-us__options-indicator indicator-'.concat(indicatorToClass(option.indicator))"
            v-text="option.indicator"
          />
          <span
            :class="
              'blood-pressure-v2-us__options-indicator-bar indicator-'
                .concat(indicatorToClass(option.indicator))
                .concat('-bar')
            "
          />
        </label>
      </li>
    </ul>

    <div class="blood-pressure-v2-us__question-title" ref="diastolicTitle">
      2. What is your diastolic number? (the bottom number)
    </div>

    <ul class="blood-pressure-v2-us__options">
      <li v-for="(option, optionIndex) in question.options.diastolic" :key="optionIndex">
        <label
          :for="'blood-pressure-v2-us__options-diastolic-'.concat(optionIndex)"
          :class="{ selected: diastolic === option.range }"
        >
          <input
            :id="'blood-pressure-v2-us__options-diastolic-'.concat(optionIndex)"
            type="radio"
            v-on:click="selectDiastolicRange(option.range)"
            :checked="diastolic === option.range"
          />
          {{ option.label }}
          <span
            :class="'blood-pressure-v2-us__options-indicator indicator-'.concat(indicatorToClass(option.indicator))"
            v-text="option.indicator"
          />
          <span
            :class="
              'blood-pressure-v2-us__options-indicator-bar indicator-'
                .concat(indicatorToClass(option.indicator))
                .concat('-bar')
            "
          />
        </label>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import QuestionnaireSubmitter from "../../mixins/poster";
import responseAdapters from "../../mixins/responseAdapters";
import { BloodPressureRangeQuestion } from "../../types";

interface QuestionData {
  systolic: string | null;
  diastolic: string | null;
  uxScrollTriggeredBefore: boolean;
  hasNotAnsweredBefore: boolean;
  responseAdapter: (question: any) => any;
}

export default Vue.extend(QuestionnaireSubmitter).extend({
  inject: ["submitUrl", "csrfHeader", "csrf"],
  props: {
    question: {
      type: Object as PropType<BloodPressureRangeQuestion>,
      required: true,
    },
    showRequired: {
      type: Boolean,
      required: true,
    },
    first: {
      type: Boolean,
      required: true,
    },
    noReadingRedirect: {
      type: String,
      required: true,
    },
  },
  data(): QuestionData {
    return {
      systolic: this.question.systolicRange,
      diastolic: this.question.diastolicRange,
      uxScrollTriggeredBefore: false,
      hasNotAnsweredBefore: false,
      responseAdapter: responseAdapters.bloodPressureRange,
    };
  },
  created() {
    this.$emit("update-next-button-status", this.complete());
    this.hasNotAnsweredBefore = !this.complete();
    this.question.redirectOnSubmit = this.redirectOnSubmit();
    this.$emit("answer", this.question, this.responseAdapter(this.question));
  },
  methods: {
    redirectOnSubmit(): string | null {
      return this.systolic === null || this.diastolic === null ? this.noReadingRedirect : null;
    },
    complete(): boolean {
      return !!(
        this.systolic &&
        Object.values(this.question.options.systolic)
          .map((systolicValue) => systolicValue.range)
          .includes(this.systolic) &&
        this.diastolic &&
        Object.values(this.question.options.diastolic)
          .map((diastolicValue) => diastolicValue.range)
          .includes(this.diastolic)
      );
    },
    saveResponsesAndProceed() {
      this.question.recentReading = !(this.systolic === null || this.diastolic === null);

      this.question.redirectOnSubmit = this.redirectOnSubmit();

      this.post(this.responseAdapter(this.question));
      if (this.hasNotAnsweredBefore) {
        this.$emit("next");
      }
    },
    selectSystolicRange(value: string) {
      this.systolic = value;
      this.question.systolicRange = this.systolic;
      if (this.complete()) {
        this.saveResponsesAndProceed();
      } else if (!this.uxScrollTriggeredBefore && window.outerWidth < 768) {
        // scroll to the diastolic options
        this.uxScrollTriggeredBefore = true;
        try {
          (this.$refs.diastolicTitle as Element).scrollIntoView?.({ behavior: "smooth" });
        } catch (error) {}
      }
    },
    selectDiastolicRange(value: string) {
      this.diastolic = value;
      this.question.diastolicRange = this.diastolic;
      if (this.complete()) {
        this.saveResponsesAndProceed();
      }
    },
    indicatorToClass(indicator: string): string {
      return indicator.toLowerCase().replace(" ", "-");
    },
  },
});
</script>
